/**
 * Avans Türü Şablonu Yönetimi (admin + muhasebeci)
 *
 * - Mevcut şablonlar + Gider Avansı kayıtlarında kullanım sayısı
 * - Yeni şablon ekle · inline rename (cascade → tüm kayıtlarda avans_turu güncellenir)
 * - Delete (in-use şablonlar 409 verir, force=true ile silebilir)
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Edit2, Trash2, Plus, CheckCircle2, AlertTriangle, GripVertical } from "lucide-react";
import { api } from "@/lib/api";

export default function AvansTuruTemplateManager({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/avans-turu-templates?include_inactive=true");
      setRows(r.data?.rows || []);
    } catch {
      toast.error("Şablonlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const notifyParent = () => { onChanged?.(); };

  const startEdit = (t) => { setEditId(t.id); setEditName(t.ad); };
  const cancelEdit = () => { setEditId(null); setEditName(""); };
  const saveEdit = async (t) => {
    const nxt = editName.trim();
    if (!nxt || nxt === t.ad) { cancelEdit(); return; }
    setBusy(true);
    try {
      const r = await api.put(`/avans-turu-templates/${t.id}`, { ad: nxt });
      const n = r.data?.renamed_rows || 0;
      toast.success(`"${t.ad}" → "${nxt}"` + (n > 0 ? ` · ${n} kayıt taşındı` : ""));
      cancelEdit();
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (t) => {
    setBusy(true);
    try {
      await api.put(`/avans-turu-templates/${t.id}`, { ad: t.ad, is_active: !t.is_active });
      toast.success(t.is_active ? "Pasif yapıldı" : "Aktif yapıldı");
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  const removeTpl = async (t) => {
    const inUse = t.usage_count || 0;
    const msg = inUse > 0
      ? `"${t.ad}" ${inUse} kayıtta kullanılıyor. Yine de silmek istiyor musun?\n(Kayıtların avans_turu alanı boşa düşmez; metin olarak kalır.)`
      : `"${t.ad}" silinsin mi?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`/avans-turu-templates/${t.id}${inUse > 0 ? "?force=true" : ""}`);
      toast.success("Silindi");
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally {
      setBusy(false);
    }
  };

  const changeColor = async (t, newColor) => {
    if (!/^#[0-9A-Fa-f]{6}$/.test(newColor)) return;
    // optimistic
    const prev = rows;
    setRows(rows.map((r) => (r.id === t.id ? { ...r, color: newColor } : r)));
    setBusy(true);
    try {
      await api.put(`/avans-turu-templates/${t.id}`, {
        ad: t.ad,
        is_active: t.is_active,
        color: newColor,
      });
      notifyParent();
    } catch (e) {
      setRows(prev);
      toast.error(e?.response?.data?.detail || "Renk güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    const nm = newName.trim();
    if (!nm) return;
    setBusy(true);
    try {
      await api.post("/avans-turu-templates", { ad: nm });
      toast.success("Eklendi");
      setNewName("");
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    } finally {
      setBusy(false);
    }
  };

  // --- Drag & Drop (native HTML5) ---
  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch { /* some browsers */ }
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== id) setDragOverId(id);
  };
  const onDragLeave = () => setDragOverId(null);
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const srcIdx = rows.findIndex((r) => r.id === sourceId);
    const tgtIdx = rows.findIndex((r) => r.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    const next = rows.slice();
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    // optimistic
    const prev = rows;
    setRows(next);
    setBusy(true);
    try {
      await api.post("/avans-turu-templates/reorder", {
        ordered_ids: next.map((r) => r.id),
      });
      toast.success("Sıralama güncellendi");
      notifyParent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sıralanamadı");
      setRows(prev); // rollback
    } finally {
      setBusy(false);
    }
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="avans-turu-tpl-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="font-bold text-slate-900">Avans Türü Şablonları</div>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={16}/></button>
        </div>

        {/* Create row */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
            placeholder="Yeni avans türü (örn. Satış Avansı)"
            className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-sm"
            data-testid="avans-turu-tpl-new"
          />
          <button
            disabled={busy || !newName.trim()}
            onClick={create}
            className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
            data-testid="avans-turu-tpl-add-btn"
          >
            <Plus size={13}/> Ekle
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-sm text-slate-500 text-center py-6">Yükleniyor...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-6">Henüz şablon yok</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="avans-turu-tpl-list">
              {rows.map((t) => {
                const isEditing = editId === t.id;
                const isDragging = dragId === t.id;
                const isDragOver = dragOverId === t.id && dragId !== t.id;
                return (
                  <li
                    key={t.id}
                    className={`py-2 flex items-center gap-2 transition-colors ${isDragging ? "opacity-40" : ""} ${isDragOver ? "bg-indigo-50 border-l-2 border-indigo-500 pl-1" : ""}`}
                    data-testid={`avans-turu-tpl-row-${t.id}`}
                    draggable={!isEditing}
                    onDragStart={(e) => onDragStart(e, t.id)}
                    onDragOver={(e) => onDragOver(e, t.id)}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => onDrop(e, t.id)}
                    onDragEnd={onDragEnd}
                  >
                    {!isEditing && (
                      <span
                        className="w-5 h-5 grid place-items-center text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing"
                        title="Sürükle ile sırala"
                        data-testid={`avans-turu-tpl-grip-${t.id}`}
                      >
                        <GripVertical size={14}/>
                      </span>
                    )}
                    {!isEditing && (
                      <label
                        className="relative w-6 h-6 rounded-full border border-slate-300 shadow-inner cursor-pointer flex-shrink-0 overflow-hidden"
                        style={{ backgroundColor: t.color || "#64748B" }}
                        title={`Renk: ${t.color || "—"} · tıkla ve değiştir`}
                        data-testid={`avans-turu-tpl-color-swatch-${t.id}`}
                      >
                        <input
                          type="color"
                          value={t.color || "#64748B"}
                          onChange={(e) => changeColor(t, e.target.value)}
                          disabled={busy}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          data-testid={`avans-turu-tpl-color-input-${t.id}`}
                        />
                      </label>
                    )}
                    {isEditing ? (
                      <>
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t); if (e.key === "Escape") cancelEdit(); }}
                          autoFocus
                          className="flex-1 px-2 py-1 border border-indigo-400 rounded text-sm"
                          data-testid={`avans-turu-tpl-edit-input-${t.id}`}
                        />
                        <button
                          onClick={() => saveEdit(t)}
                          disabled={busy}
                          className="text-xs font-semibold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded"
                          data-testid={`avans-turu-tpl-save-${t.id}`}
                        ><CheckCircle2 size={13} className="inline mr-0.5"/>Kaydet</button>
                        <button
                          onClick={cancelEdit}
                          className="text-xs text-slate-500 hover:bg-slate-100 px-2 py-1 rounded"
                        >İptal</button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-semibold ${t.is_active ? "text-slate-900" : "text-slate-400 line-through"}`}>{t.ad}</div>
                          <div className="text-[10px] text-slate-500">
                            {t.usage_count || 0} kayıt · sıra: {t.order ?? "—"}
                            {!t.is_active && <span className="ml-2 text-amber-700 font-semibold">PASİF</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => toggleActive(t)}
                          disabled={busy}
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${t.is_active ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}
                          data-testid={`avans-turu-tpl-toggle-${t.id}`}
                          title={t.is_active ? "Aktif · Pasif yapmak için tıkla" : "Pasif · Aktif yapmak için tıkla"}
                        >{t.is_active ? "AKTİF" : "PASİF"}</button>
                        <button
                          onClick={() => startEdit(t)}
                          disabled={busy}
                          className="w-7 h-7 grid place-items-center hover:bg-indigo-50 text-indigo-600 rounded"
                          data-testid={`avans-turu-tpl-edit-${t.id}`}
                          title="Yeniden Adlandır (tüm kayıtlarda otomatik güncellenir)"
                        ><Edit2 size={13}/></button>
                        <button
                          onClick={() => removeTpl(t)}
                          disabled={busy}
                          className="w-7 h-7 grid place-items-center hover:bg-rose-50 text-rose-600 rounded"
                          data-testid={`avans-turu-tpl-delete-${t.id}`}
                          title="Sil"
                        ><Trash2 size={13}/></button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-2.5 text-[11px] text-amber-900 flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5 shrink-0"/>
            <div>Satırları <strong>sürükleyip bırakarak</strong> dropdownlardaki sırayı değiştirebilirsiniz · Yeniden adlandırma mevcut Gider Avansı kayıtlarındaki <strong>avans_turu</strong> alanını da aynı anda günceller (cascade) · Pasif yapılan şablonlar yeni kayıt formunda görünmez; mevcut kayıtlarda etiketleri korunur.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
